import { useEffect, useState, useCallback } from "react";
import { ArrowLeft, Eye, Loader2, Building2, Truck, Users, Package, Send, Bell, FileText, MapPin, AlertTriangle } from "lucide-react";
import { adminFetch, saveImpersonationToken, adminFileUrl } from "@/lib/api";
import { Loader, Card } from "@/components/common";

const ROLE_LABEL = { azienda: "Azienda / Committente", vettore: "Vettore", autista: "Autista" };

function Section({ icon: Icon, title, count, children }) {
  return (
    <Card className="space-y-2">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-extrabold text-foreground">{title}</h3>
        {typeof count === "number" ? <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-bold text-muted-foreground">{count}</span> : null}
      </div>
      {children}
    </Card>
  );
}

function Row({ primary, secondary }) {
  return (
    <div className="rounded-lg border border-border bg-secondary/30 px-3 py-2">
      <p className="truncate text-[13px] font-bold text-card-foreground">{primary}</p>
      {secondary ? <p className="truncate text-[11px] text-muted-foreground">{secondary}</p> : null}
    </div>
  );
}

function Empty({ text }) {
  return <p className="text-[12px] text-muted-foreground">{text}</p>;
}

export default function AdminUserDetail({ userId, onBack }) {
  const [profile, setProfile] = useState(null);
  const [related, setRelated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impLoading, setImpLoading] = useState(false);
  const [impError, setImpError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        adminFetch(`/admin/users/${userId}`),
        adminFetch(`/admin/users/${userId}/related`),
      ]);
      setProfile(p);
      setRelated(r);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const impersonate = async () => {
    setImpLoading(true);
    setImpError("");
    try {
      const res = await adminFetch(`/admin/impersonate/${userId}`, { method: "POST", body: "{}" });
      saveImpersonationToken(res.token);
      // reload completo: l'AuthProvider ricarica l'utente impersonato e mostra l'app operativa
      window.location.href = "/";
    } catch (error) {
      setImpError(error.message || "Impossibile avviare la modalità di visualizzazione.");
      setImpLoading(false);
    }
  };

  if (loading) return <Loader />;
  if (!profile) return <Empty text="Utente non trovato." />;

  const at = profile.account_type;
  const docs = related?.documents || [];

  return (
    <div className="space-y-3" data-testid="admin-user-detail">
      <button data-testid="admin-detail-back" onClick={onBack} className="flex items-center gap-1.5 text-[13px] font-bold text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Torna all&apos;elenco
      </button>

      {/* Profilo */}
      <Card className="space-y-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-lg font-extrabold text-foreground">{profile.company_name}</p>
            <p className="truncate text-[13px] text-muted-foreground">{profile.email}</p>
          </div>
          <span className="shrink-0 rounded-full bg-primary/10 px-3 py-1 text-[11px] font-extrabold text-primary">{ROLE_LABEL[at] || at}</span>
        </div>
        <div className="grid grid-cols-2 gap-2 text-[12px] sm:grid-cols-3">
          {profile.vat_number ? <p><span className="font-bold text-card-foreground">P.IVA:</span> <span className="text-muted-foreground">{profile.vat_number}</span></p> : null}
          {profile.phone ? <p><span className="font-bold text-card-foreground">Tel:</span> <span className="text-muted-foreground">{profile.phone}</span></p> : null}
          {profile.created_at ? <p><span className="font-bold text-card-foreground">Registrato:</span> <span className="text-muted-foreground">{String(profile.created_at).slice(0, 10)}</span></p> : null}
          <p><span className="font-bold text-card-foreground">Stato:</span> <span className={profile.suspended ? "font-bold text-destructive" : "text-[hsl(var(--success))]"}>{profile.suspended ? "Sospeso" : "Attivo"}</span></p>
        </div>
        {!profile.is_admin ? (
          <div>
            <button
              data-testid="admin-impersonate-btn"
              onClick={impersonate}
              disabled={impLoading}
              className="mt-1 flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2.5 text-xs font-extrabold text-amber-950 hover:opacity-90 disabled:opacity-60"
            >
              {impLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
              Visualizza come utente
            </button>
            <p className="mt-1 text-[11px] text-muted-foreground">Sessione amministrativa temporanea e di sola consultazione. Nessuna password utente viene mostrata.</p>
            {impError ? <p className="mt-1 flex items-center gap-1 text-[12px] font-semibold text-destructive"><AlertTriangle className="h-3.5 w-3.5" />{impError}</p> : null}
          </div>
        ) : (
          <p className="text-[12px] font-semibold text-muted-foreground">Account amministratore: la modalità &quot;Visualizza come utente&quot; non è disponibile.</p>
        )}
      </Card>

      {/* Sezioni per ruolo */}
      {at === "azienda" ? (
        <>
          <Section icon={Users} title="Vettori collegati" count={(related?.carriers || []).length}>
            {(related?.carriers || []).length === 0 ? <Empty text="Nessun vettore collegato." /> : (related.carriers).map((c, i) => (
              <Row key={i} primary={c.carrier?.name || c.carrier_name || "Vettore"} secondary={`${c.carrier?.email || ""} · ${c.status || "collegato"}`} />
            ))}
          </Section>
          <Section icon={Package} title="Viaggi pubblicati" count={(related?.trips || []).length}>
            {(related?.trips || []).length === 0 ? <Empty text="Nessun viaggio pubblicato." /> : related.trips.map((t) => (
              <Row key={t.id} primary={`${t.pickup} → ${t.delivery}`} secondary={`${t.date || ""} · ${t.status_label || t.status}`} />
            ))}
          </Section>
        </>
      ) : null}

      {at === "vettore" ? (
        <>
          <Section icon={Truck} title="Flotta" count={(related?.fleet || []).length}>
            {(related?.fleet || []).length === 0 ? <Empty text="Nessun mezzo." /> : related.fleet.map((v) => (
              <Row key={v.id} primary={v.name} secondary={`${v.vehicle_type || ""} ${v.plate ? "· " + v.plate : ""}`} />
            ))}
          </Section>
          <Section icon={Users} title="Autisti" count={(related?.drivers || []).length}>
            {(related?.drivers || []).length === 0 ? <Empty text="Nessun autista." /> : related.drivers.map((d) => (
              <Row key={d.id} primary={d.name} secondary={`${d.phone || ""} ${d.driver_user_id ? "· collegato" : "· invito in attesa"}`} />
            ))}
          </Section>
          <Section icon={Package} title="Viaggi assegnati" count={(related?.trips || []).length}>
            {(related?.trips || []).length === 0 ? <Empty text="Nessun viaggio assegnato." /> : related.trips.map((t) => (
              <Row key={t.id} primary={`${t.pickup} → ${t.delivery}`} secondary={`${t.date || ""} · ${t.status_label || t.status}`} />
            ))}
          </Section>
          <Section icon={Send} title="Missioni" count={(related?.missions || []).length}>
            {(related?.missions || []).length === 0 ? <Empty text="Nessuna missione." /> : related.missions.map((m) => (
              <Row key={m.id} primary={`${m.pickup_address} → ${m.delivery_address}`} secondary={`${m.date || ""} · ${m.driver_name || ""} · ${m.status}`} />
            ))}
          </Section>
        </>
      ) : null}

      {at === "autista" ? (
        <>
          <Section icon={Truck} title="Vettore associato">
            {related?.carrier ? <Row primary={related.carrier.name} secondary={related.carrier.email} /> : <Empty text="Nessun vettore associato." />}
          </Section>
          <Section icon={Send} title="Missioni" count={(related?.missions || []).length}>
            {(related?.missions || []).length === 0 ? <Empty text="Nessuna missione." /> : related.missions.map((m) => (
              <Row key={m.id} primary={`${m.pickup_address} → ${m.delivery_address}`} secondary={`${m.date || ""} · ${m.status}`} />
            ))}
          </Section>
          <Section icon={MapPin} title="Soste / eventi" count={(related?.stops || []).length}>
            {(related?.stops || []).length === 0 ? <Empty text="Nessuna sosta registrata." /> : related.stops.map((s) => (
              <Row key={s.id} primary={s.type || s.event_type || "Evento"} secondary={String(s.created_at || "").slice(0, 16).replace("T", " ")} />
            ))}
          </Section>
        </>
      ) : null}

      {/* Documenti e notifiche (per tutti i ruoli) */}
      <Section icon={FileText} title="Documenti" count={docs.length}>
        {docs.length === 0 ? <Empty text="Nessun documento collegato." /> : docs.map((d) => (
          <a key={d.attachment_id} href={adminFileUrl(d.attachment_id)} target="_blank" rel="noreferrer" className="block rounded-lg border border-border bg-secondary/30 px-3 py-2 hover:bg-secondary">
            <p className="truncate text-[13px] font-bold text-card-foreground">{d.type} · {d.name}</p>
            <p className="truncate text-[11px] text-muted-foreground">{d.context_label || d.context} · {String(d.created_at || "").slice(0, 10)}</p>
          </a>
        ))}
      </Section>

      <Section icon={Bell} title="Notifiche" count={(related?.notifications || []).length}>
        {(related?.notifications || []).length === 0 ? <Empty text="Nessuna notifica." /> : related.notifications.map((n) => (
          <Row key={n.id} primary={n.title} secondary={`${n.message || ""} · ${String(n.created_at || "").slice(0, 10)}`} />
        ))}
      </Section>
    </div>
  );
}
